import { useState, useEffect, useCallback } from 'react';

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator?.standalone === true);

const isIOSDevice = () =>
  typeof navigator !== 'undefined' &&
  /iphone|ipad|ipod/i.test(navigator.userAgent) &&
  !window.MSStream;

/**
 * Wspólna logika instalacji PWA (DRY) — używana przez przycisk w sidebarze i baner.
 * Zwraca: { installable, isIOS, installed, available, promptInstall }.
 */
export function usePwaInstall() {
  const [promptEvent, setPromptEvent] = useState(typeof window !== 'undefined' ? window.__deferredInstallPrompt : null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const onInstallable = () => setPromptEvent(window.__deferredInstallPrompt);
    const onInstalled = () => { setInstalled(true); setPromptEvent(null); };
    window.addEventListener('pwa-installable', onInstallable);
    window.addEventListener('pwa-installed', onInstalled);
    if (window.__deferredInstallPrompt) setPromptEvent(window.__deferredInstallPrompt);
    return () => {
      window.removeEventListener('pwa-installable', onInstallable);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const isIOS = isIOSDevice();

  const promptInstall = useCallback(async () => {
    if (promptEvent) {
      promptEvent.prompt();
      try { await promptEvent.userChoice; } catch { /* noop */ }
      window.__deferredInstallPrompt = null;
      setPromptEvent(null);
      return 'prompted';
    }
    return isIOS ? 'ios' : 'none';
  }, [promptEvent, isIOS]);

  return { installable: !!promptEvent, isIOS, installed, available: !!promptEvent || isIOS, promptInstall };
}
